import React, {Component} from "react";
import {APIClient} from "../../utils/APIClient";
import {isOk, isNetworkError} from "../../utils/apiResponse";
import {API_URL} from "../../settings/local_settings";
import {AgencyHeader} from "./AgencyHeader";
import {AgencyTabs} from "./AgencyTabs";
import {ConnectionsSubTabs, CONNECTIONS_TAB_PLATFORMS} from "./ConnectionsSubTabs";
import {WebhookComponent} from "./webhook/WebhookComponent";
import {
    ANALYTICS_CHANNEL,
    SEARCH_CONSOLE_CHANNEL,
    GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, DV360_CHANNEL, MERCADO_ADS_CHANNEL,
    SHOPIFY_CHANNEL,
    TIENDA_NUBE_CHANNEL,
    KLAVIYO_CHANNEL,
    WOOCOMMERCE_CHANNEL,
    VTEX_CHANNEL,
    ESCALAFY_CHANNEL
} from "../channels/channels";
import {ChannelCredentialsList} from "./connections/ChannelCredentialsList";
import {FacebookReconnectModal} from "./connections/FacebookReconnectModal";
import {AnalyticsComponent} from "./analytics/AnalyticsComponent";
import {EcommerceConnectionsComponent} from "./connections/EcommerceConnectionsComponent";
import {CreateControlLinesModal} from "./link_acocunts/CreateControlLinesModal";
import {withRouterParams} from "../../utils/withRouterParams";
import { withTranslation } from 'react-i18next';
import {toast} from "react-toastify";
import {LoadErrorState} from "../elements/LoadErrorState";
import {Spinner} from "../Spinner";
import {Ga4AttributionClassicNote} from "../ga4_attribution/Ga4AttributionClassicNote";
import {MercadoAdsConnectNotice} from "../channels/mercado_ads/MercadoAdsConnectNotice";
import {announceReconnectNotice, takeDeferredReconnectNotice} from "./connections/reconnectAccountText";

// Mercado Ads se dibuja sólo si el backend lo manda en `channels_allowed` (flag por agencia).
const PAID_MEDIA_CHANNELS = [GOOGLE_CHANNEL, FACEBOOK_CHANNEL, TIKTOK_CHANNEL, LINKEDIN_CHANNEL, DV360_CHANNEL,
    MERCADO_ADS_CHANNEL];

const SYNC_POLL_INTERVAL_MS = 8000;
const SYNC_POLL_MAX_DURATION_MS = 5 * 60 * 1000;
const CREDENTIALS_STATE_KEYS = [
    'googleCredentials', 'facebookCredentials', 'tiktokCredentials',
    'linkedinCredentials', 'ga4Credentials', 'gscCredentials', 'dv360Credentials', 'escalafyCredentials',
    'mercadoAdsCredentials',
];

class AgencyConnectionsConfigurationContent extends Component {
    constructor(props) {
        super(props);
        this.state = {
            googleAccountAlreadyLinked: null,
            facebookAccountAlreadyLinked: null,
            loading: true,
            // Error de la CARGA de la pantalla (null = sin error). Sin esto, un fallo del GET dejaba
            // los canales sin dibujar y Looker/Nodo de Datos con el spinner para siempre, como si la
            // agencia no tuviera canales (QA-VIS-linkedin-01).
            loadError: null,
            channelsAllowed: [],
            saving: false,
            showModalForChannel: null,
            showControlLinesModal: false,
            showFacebookReconnectModal: false,
            facebookReconnectAccId: null,
            controlLinesAdAccounts: [],
            // Multi-credencial
            googleCredentials: null,
            facebookCredentials: null,
            tiktokCredentials: null,
            linkedinCredentials: null,
            ga4Credentials: null,
            gscCredentials: null,
            dv360Credentials: null,
            escalafyCredentials: null,
            mercadoAdsCredentials: null,
        };

        this.handleGA4ConnectionDeleted = this.handleGA4ConnectionDeleted.bind(this);
        this.handleAdAccountRemove = this.handleAdAccountRemove.bind(this);
        this.handleCredentialRemoved = this.handleCredentialRemoved.bind(this);
        this.handleControlLinesModalFinish = this.handleControlLinesModalFinish.bind(this);
        this.retryLoad = this.retryLoad.bind(this);
    }

    retryLoad() {
        // Vuelve al loader antes de pedir de nuevo, así el clic se ve.
        this.setState({loadError: null, loading: true}, () => this.updateConnectionsInformation());
    }

    _handleLoadFailure() {
        // Sólo la carga de la pantalla muestra el error: un refresco de fondo (polling del sync,
        // re-lectura tras una acción) que falla conserva lo que ya se ve, en silencio.
        if (this.state.loading) {
            this.setState({loadError: 'load'});
        }
    }

    redirectToAgencyRegisterPage() {
        this.props.history.push('/agency-register');
    }

    componentDidMount() {
        const params = new URLSearchParams(this.props.location.search);
        const justConnectedChannel = params.get('just_connected');
        const reconnectAdAccountId = params.get('reconnect_ad_account_id');
        const reconnectCredentialId = params.get('credential');
        const reconnectPendingChannel = params.get('reconnect_pending');

        this._syncPollingTimeout = null;
        this._syncPollingStartedAt = null;

        this.updateConnectionsInformation(justConnectedChannel, reconnectAdAccountId, reconnectCredentialId);
        // Meta vuelve recargando la página: el aviso de su reconexión espera acá (`deferReconnectNotice`).
        announceReconnectNotice(takeDeferredReconnectNotice());

        if (justConnectedChannel) {
            this.showCreateControlLinesModal(justConnectedChannel);
        }

        // Meta reconecta con el SDK JS de Facebook (popup), no con un redirect OAuth de página
        // completa. El botón "Reconectar" navega a `?reconnect_pending=FACEBOOK`; acá leemos el param
        // y abrimos el modal con el botón de Facebook. Limpiamos el query param para que un refresh
        // no reabra el modal.
        // `reconnect_acc_id` puede no venir, y es legítimo: PaidMediaReconnectFlow manda al usuario acá
        // para conectar una cuenta NUEVA (con el nombre que tipeó en `pending_credential_label`), que es
        // un alta, no una reconexión. Sin id el backend resuelve solo: si esa persona de Facebook ya
        // sostiene una conexión, la reconecta; si no, la da de alta.
        if (reconnectPendingChannel === FACEBOOK_CHANNEL) {
            this.setState({
                showFacebookReconnectModal: true,
                facebookReconnectAccId: params.get('reconnect_acc_id'),
            });
            window.history.replaceState({}, '', this.props.location.pathname);
        }
    }

    componentWillUnmount() {
        this._stopSyncPolling();
    }

    _stopSyncPolling() {
        if (this._syncPollingTimeout) {
            clearTimeout(this._syncPollingTimeout);
            this._syncPollingTimeout = null;
        }
        this._syncPollingStartedAt = null;
    }

    _anyCredentialSyncing() {
        return CREDENTIALS_STATE_KEYS.some((key) => {
            const list = this.state[key];
            return Array.isArray(list) && list.some((cred) => cred && cred.is_syncing);
        });
    }

    _scheduleSyncPollIfNeeded() {
        if (!this._anyCredentialSyncing()) {
            this._stopSyncPolling();
            return;
        }
        if (this._syncPollingStartedAt === null) {
            this._syncPollingStartedAt = Date.now();
        }
        if (Date.now() - this._syncPollingStartedAt > SYNC_POLL_MAX_DURATION_MS) {
            this._stopSyncPolling();
            return;
        }
        if (this._syncPollingTimeout) {
            clearTimeout(this._syncPollingTimeout);
        }
        this._syncPollingTimeout = setTimeout(() => {
            this._syncPollingTimeout = null;
            this.updateConnectionsInformation();
        }, SYNC_POLL_INTERVAL_MS);
    }

    showCreateControlLinesModal(channel) {
        new APIClient(API_URL + 'agency/connections/'+channel+'/new/').call('GET').then((response) => {
            if (response && response.status === 'OK') {
                const data = response.data;
                if (data.first_time_on_channel) {
                    this.setState({
                        showControlLinesModal: true,
                        controlLinesAdAccounts: data.ad_accounts
                    });
                }
            }
        });
    }

    handleControlLinesModalFinish(count) {
        const { t } = this.props;
        this.setState({ showControlLinesModal: false });
        if (count > 0) {
            toast.success(t('connections_component.control_lines_created', {count: count}));
        }
    }

    updateConnectionsInformation(reconnectChannel, reconnectAdAccountId, reconnectCredentialId) {
        new APIClient(API_URL + 'agency/connections/').call('GET').then((data) => {
            if (data.status === 'ERROR_NO_AGENCY') {
                alert(data['error_message']);
                this.redirectToAgencyRegisterPage()
            } else if (data.status === 'OK') {
                this.setState({
                    googleAccountAlreadyLinked: data.data.google_account_already_linked,
                    ga4AccountAlreadyLinked: data.data.ga4_account_already_linked,
                    facebookAccountAlreadyLinked: data.data.facebook_account_already_linked,
                    tiktokAccountAlreadyLinked: data.data.tiktok_account_already_linked,
                    linkedinAccountAlreadyLinked: data.data.linkedin_account_already_linked,
                    useGoogleScriptsForGA4: data.data.use_ga4_thou_scripts,
                    channelsAllowed: data.data.channels_allowed,
                    shopifyAdAccounts: data.data.shopify_ad_accounts_data,
                    tiendaNubeAdAccounts: data.data.tienda_nube_ad_accounts_data,
                    klaviyoAdAccounts: data.data.klaviyo_ad_accounts_data,
                    woocommerceAdAccounts: data.data.woocommerce_ad_accounts_data,
                    vtexAdAccounts: data.data.vtex_ad_accounts_data,
                    // Multi-credencial
                    googleCredentials: data.data.google_credentials || [],
                    facebookCredentials: data.data.facebook_credentials || [],
                    tiktokCredentials: data.data.tiktok_credentials || [],
                    linkedinCredentials: data.data.linkedin_credentials || [],
                    ga4Credentials: data.data.ga4_credentials || [],
                    gscCredentials: data.data.gsc_credentials || [],
                    dv360Credentials: data.data.dv360_credentials || [],
                    escalafyCredentials: data.data.escalafy_credentials || [],
                    mercadoAdsCredentials: data.data.mercado_ads_credentials || [],
                    loading: false
                }, () => {
                    if (reconnectChannel && reconnectAdAccountId) {
                        this.autoReassignCredential(reconnectChannel, reconnectAdAccountId, reconnectCredentialId);
                    }
                    this._scheduleSyncPollIfNeeded();
                });
            } else {
                this._handleLoadFailure();
            }
        }).catch(() => this._handleLoadFailure());
    }

    // A la conexión que devolvió el pedido (`&credential=`); con un backend viejo que no la devuelve, a
    // la más nueva del canal. Con el backend de la tanda «Reconexión de pauta» ya la reasignó él.
    autoReassignCredential(channel, adAccountId, credentialId = null) {
        const { t } = this.props;
        const credentialsMap = {
            [GOOGLE_CHANNEL]: this.state.googleCredentials,
            [FACEBOOK_CHANNEL]: this.state.facebookCredentials,
            [TIKTOK_CHANNEL]: this.state.tiktokCredentials,
            [LINKEDIN_CHANNEL]: this.state.linkedinCredentials,
            [DV360_CHANNEL]: this.state.dv360Credentials,
            [MERCADO_ADS_CHANNEL]: this.state.mercadoAdsCredentials,
        };

        const credentials = credentialsMap[channel];
        if (!credentials || credentials.length === 0) return;

        const targetCredential = credentials.find((cred) => String(cred.id) === String(credentialId))
            || credentials.reduce((newest, cred) => cred.id > newest.id ? cred : newest);

        new APIClient(API_URL + `agency/ad-accounts/${adAccountId}/reassign-credential`).call('POST', {
            credential_id: targetCredential.id,
        }).then((data) => {
            // `status !== 'OK'`, no `=== 'ERROR'`: con ese literal, un `NETWORK_ERROR` o un código
            // tipado del backend caía en el `else` y la pantalla FESTEJABA -- «reasignación
            // exitosa» sobre una reasignación que no ocurrió.
            if (!isOk(data)) {
                // Igual que en el modal de permisos: el fallo de red lleva copy propia por i18n,
                // porque el texto que trae el objeto de `APIClient` esta hardcodeado en espanol.
                toast.error(isNetworkError(data)
                    ? t('reconnect_modal.error_network')
                    : (data.error_message || t('reconnect_modal.error_no_access')));
            } else {
                toast.success(t('reconnect_modal.auto_reassign_success'));
                this.updateConnectionsInformation();
            }
        }).catch(() => {
            toast.error(t('reconnect_modal.error_no_access'));
        });
    }

    handleGA4ConnectionDeleted() {
        const { t } = this.props;
        alert(t('agency_connections_configuration.ga4_disconnected_successfully'));
        this.setState({
            ga4AccountAlreadyLinked: false
        });
    }

    handleCredentialRemoved(channel, credentialId) {
        this.setState({ saving: true });
        new APIClient(`${API_URL}agency/credentials/${channel}/${credentialId}/`).call('DELETE').then((data) => {
            this.setState({ saving: false });
            if (data.status === 'ERROR') {
                alert(data.error_message);
            } else if (data.status === 'OK') {
                this.updateConnectionsInformation();
            }
        }).catch(() => {
            this.setState({ saving: false });
        });
    }

    handleAdAccountRemove(adAccountId) {
        this.setState({ saving: true });
        new APIClient(`${API_URL}agency/ad-accounts/${adAccountId}/disconnect`).call('POST').then((data) => {
            if (data.status === 'ERROR') {
                this.setState({ saving: false });
                alert(data.error_message);
            } else if (data.status === 'OK') {
                this.updateConnectionsInformation();
            }
        })
    }

    render() {
        return (
            <section className="section-content">
                <AgencyHeader/>
                <AgencyTabs activeTab="-sixth"/>
                <ConnectionsSubTabs activeSubTab={CONNECTIONS_TAB_PLATFORMS}/>
                {this.state.loadError !== null ? this.renderLoadError() : this.renderConnections()}
            </section>
        )
    }

    renderLoadError() {
        const {t} = this.props;
        return (
            <LoadErrorState title={t('agency_connections_configuration.load_error')}
                            text={t('agency_connections_configuration.load_error_explanation')}
                            retryLabel={t('agency_connections_configuration.load_error_retry')}
                            onRetry={this.retryLoad}/>
        );
    }

    renderConnections() {
        return (
            <>
                {/* Mientras carga, los canales no se dibujan (salen de `channels_allowed`). Las cajas
                    de Data Studio y del Nodo de Datos eran las únicas con spinner, y se fueron a
                    Exportación: sin esto, la pestaña quedaba muda hasta que llegaba la respuesta. */}
                {this.state.loading && (
                    <div className="box-loader">
                        <Spinner color="positive"/>
                    </div>
                )}
                {/* La vuelta de conectar Mercado Ads: lo que se conectó, o el error con qué hacer. */}
                {!this.state.loading && (
                    <MercadoAdsConnectNotice wrapper={(card) => (
                        <div className="section-body h-items" style={{paddingBottom: '0em'}}>
                            <div className="section-body-item -wider" style={{minWidth: 0}}>{card}</div>
                        </div>
                    )}/>
                )}
                {this.renderPaidMediaConnections()}
                {/* La regla de cruce con GA4 se configura sólo en la 2.1: acá, una línea que lo dice. */}
                {!this.state.loading && <Ga4AttributionClassicNote/>}
                {this.renderEcommerceConnections()}
                {this.renderProfitabilityConnections()}
                <div className="section-body -wider h-items" style={{paddingBottom:'0em'}}>
                    {this.renderGA4Component()}
                </div>
                <div className="section-body -wider h-items" style={{paddingBottom:'0em'}}>
                    {this.renderGSCComponent()}
                </div>
                {/* El conector de Data Studio y el Nodo de Datos ya no van acá: SACAN datos de
                    Dashbo, y viven en la pestaña Exportación (`AgencyConnectionsExportContent`). Una
                    sola caja por pestaña, no la misma en las dos. */}
                <div className="section-body -wider h-items" style={{paddingBottom:'0em'}}>
                    <WebhookComponent />
                </div>
                <CreateControlLinesModal
                    isOpen={this.state.showControlLinesModal}
                    toggle={() => this.setState({showControlLinesModal: !this.state.showControlLinesModal})}
                    adAccounts={this.state.controlLinesAdAccounts}
                    onFinish={this.handleControlLinesModalFinish}
                />
                <FacebookReconnectModal
                    isOpen={this.state.showFacebookReconnectModal}
                    reconnectAccId={this.state.facebookReconnectAccId}
                    onClose={() => this.setState({showFacebookReconnectModal: false})}
                />
            </>
        )
    }

    renderPaidMediaConnections() {
        const credentialsMap = {
            [GOOGLE_CHANNEL]: this.state.googleCredentials,
            [FACEBOOK_CHANNEL]: this.state.facebookCredentials,
            [TIKTOK_CHANNEL]: this.state.tiktokCredentials,
            [LINKEDIN_CHANNEL]: this.state.linkedinCredentials,
            [DV360_CHANNEL]: this.state.dv360Credentials,
            [MERCADO_ADS_CHANNEL]: this.state.mercadoAdsCredentials,
        };

        return PAID_MEDIA_CHANNELS.map(channel => {
            if (!this.state.channelsAllowed.includes(channel))
                return null;

            return (
                <div key={channel} className="section-body h-items" style={{paddingBottom: '0em'}}>
                    <ChannelCredentialsList
                        channel={channel}
                        credentials={credentialsMap[channel]}
                        onCredentialRemoved={this.handleCredentialRemoved}
                        onCredentialUpdated={() => this.updateConnectionsInformation()}
                    />
                </div>
            );
        });
    }

    renderEcommerceConnections() {
        const ecommerceChannels = [];

        if (this._agencyHasEcommerceChannel(SHOPIFY_CHANNEL)) {
            ecommerceChannels.push(
                <div key={SHOPIFY_CHANNEL} className="section-body h-items" style={{paddingBottom: '0em'}}>
                    <EcommerceConnectionsComponent channel={SHOPIFY_CHANNEL}
                                                   onAdAccountConnectionDeleted={this.handleAdAccountConnectionDeleted}
                                                   adAccountsDatas={this.state.shopifyAdAccounts}
                                                   onAdAccountConnectionCreated={this.handleAdAccountConnectionCreated}
                                                   doAdAccountRemove={this.handleAdAccountRemove}
                                                   isSaving={this.state.saving}
                    />
                </div>
            );
        }

        if (this._agencyHasEcommerceChannel(TIENDA_NUBE_CHANNEL)) {
            ecommerceChannels.push(
                <div key={TIENDA_NUBE_CHANNEL} className="section-body h-items" style={{paddingBottom: '0em'}}>
                    <EcommerceConnectionsComponent channel={TIENDA_NUBE_CHANNEL}
                                                   onAdAccountConnectionDeleted={this.handleAdAccountConnectionDeleted}
                                                   adAccountsDatas={this.state.tiendaNubeAdAccounts}
                                                   onAdAccountConnectionCreated={this.handleAdAccountConnectionCreated}
                                                   doAdAccountRemove={this.handleAdAccountRemove}
                                                   isSaving={this.state.saving}
                    />
                </div>
            );
        }

        if (this._agencyHasEcommerceChannel(KLAVIYO_CHANNEL)) {
            ecommerceChannels.push(
                <div key={KLAVIYO_CHANNEL} className="section-body h-items" style={{paddingBottom: '0em'}}>
                    <EcommerceConnectionsComponent channel={KLAVIYO_CHANNEL}
                                                   onAdAccountConnectionDeleted={this.handleAdAccountConnectionDeleted}
                                                   adAccountsDatas={this.state.klaviyoAdAccounts}
                                                   onAdAccountConnectionCreated={this.handleAdAccountConnectionCreated}
                                                   doAdAccountRemove={this.handleAdAccountRemove}
                                                   isSaving={this.state.saving}
                    />
                </div>
            );
        }

        if (this._agencyHasEcommerceChannel(WOOCOMMERCE_CHANNEL)) {
            ecommerceChannels.push(
                <div key={WOOCOMMERCE_CHANNEL} className="section-body h-items" style={{paddingBottom: '0em'}}>
                    <EcommerceConnectionsComponent channel={WOOCOMMERCE_CHANNEL}
                                                   onAdAccountConnectionDeleted={this.handleAdAccountConnectionDeleted}
                                                   adAccountsDatas={this.state.woocommerceAdAccounts}
                                                   onAdAccountConnectionCreated={this.handleAdAccountConnectionCreated}
                                                   doAdAccountRemove={this.handleAdAccountRemove}
                                                   isSaving={this.state.saving}
                    />
                </div>
            );
        }

        if (this._agencyHasEcommerceChannel(VTEX_CHANNEL)) {
            ecommerceChannels.push(
                <div key={VTEX_CHANNEL} className="section-body h-items" style={{paddingBottom: '0em'}}>
                    <EcommerceConnectionsComponent channel={VTEX_CHANNEL}
                                                   onAdAccountConnectionDeleted={this.handleAdAccountConnectionDeleted}
                                                   adAccountsDatas={this.state.vtexAdAccounts}
                                                   onAdAccountConnectionCreated={this.handleAdAccountConnectionCreated}
                                                   doAdAccountRemove={this.handleAdAccountRemove}
                                                   isSaving={this.state.saving}
                    />
                </div>
            );
        }

        return ecommerceChannels;
    }

    // «Rentabilidad del negocio» (Escalafy): la habilita el módulo de Economía, y el backend lo dice
    // sumando ESCALAFY a `channels_allowed`. Se nombra por la familia, no por el canal.
    renderProfitabilityConnections() {
        const { t } = this.props;
        if (!this.state.channelsAllowed || !this.state.channelsAllowed.includes(ESCALAFY_CHANNEL))
            return null;

        return (
            <div className="section-body h-items" style={{paddingBottom: '0em'}}>
                <ChannelCredentialsList
                    channel={ESCALAFY_CHANNEL}
                    className="agency-connections-profitability"
                    title={t('agency_connections.profitability_title')}
                    subtitle={t('agency_connections.profitability_subtitle')}
                    newConnectionLabel={t('agency_connections.escalafy_connect')}
                    credentials={this.state.escalafyCredentials}
                    onCredentialRemoved={this.handleCredentialRemoved}
                    onCredentialUpdated={() => this.updateConnectionsInformation()}
                />
            </div>
        );
    }

    _agencyHasEcommerceChannel(channel) {
        if (this.state.channelsAllowed === null)
            return false;

        return this.state.channelsAllowed.includes(channel);
    }

    renderGA4Component() {
        if (this.state.useGoogleScriptsForGA4)
            return <AnalyticsComponent />

        // GA4 usa ChannelCredentialsList (multi-credencial)
        if (this.state.channelsAllowed.includes(ANALYTICS_CHANNEL)) {
            return <ChannelCredentialsList
                channel={ANALYTICS_CHANNEL}
                credentials={this.state.ga4Credentials}
                onCredentialRemoved={this.handleCredentialRemoved}
                onCredentialUpdated={() => this.updateConnectionsInformation()}
            />;
        }
        return null;
    }

    renderGSCComponent() {
        if (this.state.channelsAllowed.includes(SEARCH_CONSOLE_CHANNEL)) {
            return <ChannelCredentialsList
                channel={SEARCH_CONSOLE_CHANNEL}
                credentials={this.state.gscCredentials}
                onCredentialRemoved={this.handleCredentialRemoved}
                onCredentialUpdated={() => this.updateConnectionsInformation()}
            />;
        }
        return null;
    }
}

export default withRouterParams(withTranslation()(AgencyConnectionsConfigurationContent));
